import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * State interface for the UI configuration.
 */
export interface UiConfigState {
  /** Configuration for resizable elements in the UI. */
  resizable: { sidebarPanel: { defaultSize: number; minSize: number; maxSize: number } };
  /** Configuration for showing/hiding different types of toast notifications. */
  showToasts: { success: boolean; info: boolean; warning: boolean; error: boolean };
}

const defaultState: UiConfigState = {
  resizable: { sidebarPanel: { defaultSize: 25, minSize: 20, maxSize: 40 } },
  showToasts: { success: true, info: true, warning: true, error: true },
};

const slice = createSlice({
  name: 'uiConfig',
  initialState: defaultState,
  reducers: {
    /**
     * Updates the configuration of the sidebar panel, such as its size.
     * @param state - The current state.
     * @param action - The action containing the partial sidebar panel configuration.
     */
    updateSidebarPanelConfig: (state, action) => {
      state.resizable.sidebarPanel = { ...state.resizable.sidebarPanel, ...action.payload };
    },
    /**
     * Sets the visibility of toast notifications.
     * Can enable/disable all toasts or specific types (success, info, warning, error).
     * @param state - The current state.
     * @param action - The action containing a boolean to set all, or an object for specific types.
     */
    setToastEnabled: (
      state,
      action: PayloadAction<
        boolean | { success?: boolean; info?: boolean; warning?: boolean; error?: boolean }
      >
    ) => {
      if (typeof action.payload === 'boolean') {
        state.showToasts = {
          success: action.payload,
          info: action.payload,
          warning: action.payload,
          error: action.payload,
        };
      } else {
        state.showToasts = { ...state.showToasts, ...action.payload };
      }
    },
  },
});

export const { updateSidebarPanelConfig, setToastEnabled } = slice.actions;
export default slice.reducer;
